import { NAVY } from '../../styles/tokens';

function fmt(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const plural = (n, s = 's') => (n !== 1 ? s : '');

// Gastos B — confirmación antes de un export por lote + marca.
//
// V74: el resumen pasó de ser POR PROYECTO a ser POR REQUISICIÓN, y el número es editable.
// Eso ya es información nueva aunque nadie edite: hasta acá nadie podía ver qué ítems iban a
// compartir cheque. No se pierde el corte por proyecto — cada requisición ES un proyecto y un
// proveedor, así que la fila nueva dice más que la vieja; tener las dos sería ruido.
//
// 🔴 EL CASO NORMAL SIGUE SIENDO DE UN CLIC. Los campos vienen precargados con el número
// propuesto y no hay que tocarlos; el llamador manda como override solo lo que difiere. Sin
// modo edición, sin guardar por fila, sin campo obligatorio.
//
// ⚠ ESTE COMPONENTE NO VALIDA NADA. Ni formato, ni serie, ni si el número es anterior al
// contador. El servidor es el único que decide, sus mensajes ya están redactados y nombran el
// problema, y devuelve `groupKey` para poder anclar cada error a su fila (`fieldErrors`).
// Reimplementar acá cualquiera de esas reglas sería una segunda copia que se separa de la
// primera en cuanto una cambie.
export default function ExportConfirmModal({
  loading = false,
  previewError = null,
  pending = [],
  assigned = [],
  numbers = {},
  onChangeNumber,
  fieldErrors = {},
  generalError = null,
  receiptCount = 0,
  excludedGroups = [],
  uncodedGroups = [],
  includeExported = false,
  exporting = false,
  onConfirm,
  onCancel,
}) {
  const totalSum = [...pending, ...assigned].reduce((s, g) => s + (Number(g.total) || 0), 0);
  const puedeConfirmar = !loading && !previewError && pending.length + assigned.length > 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#fff',
        border: `1.5px solid ${NAVY}`,
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(27,58,107,0.18)',
        padding: '28px 32px',
        maxWidth: 620,
        width: '94%',
        maxHeight: '90vh',
        overflowY: 'auto',
        animation: 'fadeUp 0.2s ease',
      }}>
        <div style={{ fontSize: 18, fontWeight: 'bold', color: NAVY, marginBottom: 6 }}>
          Confirmar exportación a Sage
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: '#64748b', padding: '24px 0' }}>
            Calculando las requisiciones…
          </div>
        ) : previewError ? (
          <div style={{
            margin: '10px 0 18px', padding: '10px 12px', borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca', fontSize: 13, color: '#991b1b',
          }}>
            {previewError}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 18 }}>
              Se exportarán <strong>{receiptCount}</strong> recibo{plural(receiptCount)} ({fmt(totalSum)}) en{' '}
              <strong>{pending.length + assigned.length}</strong> requisici{pending.length + assigned.length === 1 ? 'ón' : 'ones'} y
              se marcarán como <strong>pagados</strong> y <strong>exportados</strong>.
              {includeExported && ' Incluye recibos ya exportados.'}
            </div>

            {/* ── Pendientes: editable ─────────────────────────────────────── */}
            {pending.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
                  {pending.length} requisici{pending.length === 1 ? 'ón' : 'ones'} — una por cheque
                </div>
                <div style={{ marginBottom: 8, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  {pending.map((g, i) => {
                    const err = fieldErrors[g.groupKey];
                    return (
                      <div key={g.groupKey} style={{
                        padding: '10px 12px',
                        background: err ? '#fef2f2' : (i % 2 ? '#f8fafc' : '#fff'),
                        borderTop: i ? '1px solid #eef2f7' : 'none',
                      }}>
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                          justifyContent: 'space-between', gap: 10,
                        }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, wordBreak: 'break-word' }}>
                              {g.sageVendorId || '—'} · {g.projectCode || 'Sin proyecto'}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {g.itemCount} ítem{plural(g.itemCount)} · {fmt(g.total)}
                            </div>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}>
                            Requisición
                            <input
                              value={numbers[g.groupKey] ?? ''}
                              placeholder={g.proposedNumber}
                              onChange={e => onChangeNumber(g.groupKey, e.target.value)}
                              disabled={exporting}
                              aria-label={`Número de requisición para ${g.sageVendorId} ${g.projectCode}`}
                              style={{
                                width: 150, padding: '5px 8px', fontSize: 13,
                                fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                                border: `1px solid ${err ? '#dc2626' : '#cbd5e1'}`,
                                borderRadius: 6, color: NAVY, background: '#fff',
                              }}
                            />
                          </label>
                        </div>
                        {err && (
                          <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6 }}>{err}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 18 }}>
                  Los números sin editar se confirman al exportar. Edite solo si la requisición ya
                  se hizo fuera del portal y el número tiene que ser otro.
                </div>
              </>
            )}

            {/* ── Ya numeradas: solo lectura ───────────────────────────────── */}
            {assigned.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                  Ya numeradas — se reutilizan, no generan cheque nuevo
                </div>
                <div style={{
                  marginBottom: 18, border: '1px dashed #e2e8f0', borderRadius: 8, overflow: 'hidden',
                }}>
                  {assigned.map(a => (
                    <div key={a.number} style={{
                      display: 'grid', gridTemplateColumns: '150px 1fr auto', gap: 10,
                      padding: '7px 12px', fontSize: 12.5, color: '#64748b', background: '#fbfcfe',
                    }}>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: NAVY }}>
                        {a.number}
                      </span>
                      <span style={{ wordBreak: 'break-word' }}>
                        {a.sageVendorId || '—'} · {a.projectCode || 'Sin proyecto'}
                      </span>
                      <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {a.itemCount} ítem{plural(a.itemCount)} · {fmt(a.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {generalError && (
          <div style={{
            marginBottom: 18, padding: '10px 12px', borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca', fontSize: 12.5, color: '#991b1b',
          }}>
            {generalError}
          </div>
        )}

        {/* Excluidos: sin Proveedor Sage */}
        {excludedGroups.length > 0 && (
          <div style={{
            marginBottom: 18, padding: '10px 12px', borderRadius: 8,
            background: '#fffbeb', border: '1px solid #fde68a',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
              ⚠ {excludedGroups.length} recibo{plural(excludedGroups.length)} excluido{plural(excludedGroups.length)} (sin Proveedor Sage) — no se exportará{plural(excludedGroups.length, 'n')}:
            </div>
            <div style={{ fontSize: 12, color: '#92400e', wordBreak: 'break-word' }}>
              {excludedGroups.map(g => g.receiptNumber || `#${g.receiptId}`).join(', ')}
            </div>
          </div>
        )}

        {/* Excluidos: falta codificación (V49) */}
        {uncodedGroups.length > 0 && (
          <div style={{
            marginBottom: 18, padding: '10px 12px', borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>
              ⚠ {uncodedGroups.length} recibo{plural(uncodedGroups.length)} excluido{plural(uncodedGroups.length)} (falta codificación: Tarea / Centro de Costo / Código GL) — no se exportará{plural(uncodedGroups.length, 'n')}:
            </div>
            <div style={{ fontSize: 12, color: '#991b1b', wordBreak: 'break-word' }}>
              {uncodedGroups.map(g => g.receiptNumber || `#${g.receiptId}`).join(', ')}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onConfirm}
            disabled={exporting || !puedeConfirmar}
            style={{
              flex: 1, background: (exporting || !puedeConfirmar) ? '#e0e0e0' : NAVY,
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 0', fontWeight: 'bold', fontSize: 14,
              cursor: (exporting || !puedeConfirmar) ? 'not-allowed' : 'pointer',
              fontFamily: 'Calibri, sans-serif',
            }}
          >
            {exporting ? 'Exportando…' : 'Confirmar y exportar'}
          </button>
          <button
            onClick={onCancel}
            disabled={exporting}
            style={{
              flex: 1, background: '#fff', color: NAVY,
              border: `1.5px solid ${NAVY}`, borderRadius: 8,
              padding: '10px 0', fontWeight: 'bold', fontSize: 14,
              cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: 'Calibri, sans-serif',
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * El segundo paso de `allowBelowCounter`.
 *
 * 🔴 NO ES UNA CASILLA EN EL FORMULARIO, y esa es la decisión: una casilla se puede marcar
 * antes de que el riesgo exista, y entonces se marca una vez y se ignora para siempre. Este
 * diálogo **solo se puede abrir cuando el servidor ya rechazó el número**, nombra el número
 * concreto y el último emitido concreto, y su botón dice qué va a pasar en vez de "Aceptar".
 *
 * El riesgo que enuncia es real y no es hipotético: el portal no ve las requisiciones hechas
 * a mano en Sage, así que un número anterior al contador puede estar ya usado allá.
 */
export function BelowCounterDialog({ pedido, exporting = false, onAccept, onCancel }) {
  if (!pedido) return null;
  const { numero, etiqueta, ultimoEmitido } = pedido;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    }}>
      <div style={{
        background: '#fff', border: '1.5px solid #b45309', borderRadius: 14,
        boxShadow: '0 4px 24px rgba(0,0,0,0.2)', padding: '24px 28px',
        maxWidth: 480, width: '92%',
      }}>
        <div style={{ fontSize: 16, fontWeight: 'bold', color: '#92400e', marginBottom: 10 }}>
          Este número puede estar usado
        </div>
        <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6, marginBottom: 16 }}>
          Escribiste <strong style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{numero}</strong>
          {etiqueta ? <> para <strong>{etiqueta}</strong></> : null}.
          <br /><br />
          El último número emitido por el portal en esa serie es el <strong>{ultimoEmitido}</strong>.
          Un número anterior es correcto si la requisición se hizo a mano hace tiempo — pero
          también puede estar <strong>ya usado en Sage</strong> por una requisición que el portal
          no ve, y en ese caso quedarían dos cheques con el mismo número.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onAccept}
            disabled={exporting}
            style={{
              flex: 1, background: exporting ? '#e0e0e0' : '#b45309', color: '#fff',
              border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 'bold', fontSize: 13.5,
              cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: 'Calibri, sans-serif',
            }}
          >
            {exporting ? 'Exportando…' : `Usar ${numero} igual`}
          </button>
          <button
            onClick={onCancel}
            disabled={exporting}
            style={{
              flex: 1, background: '#fff', color: NAVY, border: `1.5px solid ${NAVY}`,
              borderRadius: 8, padding: '10px 0', fontWeight: 'bold', fontSize: 13.5,
              cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: 'Calibri, sans-serif',
            }}
          >
            Volver y cambiarlo
          </button>
        </div>
      </div>
    </div>
  );
}
